import { useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { copyNoteToEhr } from "@/lib/copyToEhr"
import { useToast } from "@/components/ui/use-toast"
import { tcmApi, tcmErrorMessage } from "../api"
import { tcmKeys } from "../hooks"

/** Fetch the case's latest note and put it on the clipboard in both flavors. */
export function useCopyTcmNote() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [copyingId, setCopyingId] = useState<string | null>(null)

  const copy = async (episodeId: string, ehr: string) => {
    setCopyingId(episodeId)
    try {
      const note = await queryClient.fetchQuery({
        queryKey: tcmKeys.note(episodeId),
        queryFn: () => tcmApi.getNote(episodeId),
        staleTime: 5 * 60 * 1000,
      })
      const outcome = await copyNoteToEhr(note)
      if (outcome === "failed") {
        toast({
          title: "Could not reach the clipboard",
          description: "Your browser blocked the copy. Check clipboard permissions for this site and try again.",
          variant: "destructive",
        })
        return
      }
      toast({
        title: outcome === "formatted" ? "TCM note copied" : "Copied as plain text",
        description:
          outcome === "formatted"
            ? `Paste it into the visit note in ${ehr}.`
            : "Formatting could not be copied, but the note still reads correctly.",
      })
    } catch (err) {
      toast({ title: "Could not load the note", description: tcmErrorMessage(err), variant: "destructive" })
    } finally {
      setCopyingId(null)
    }
  }

  return { copy, copyingId }
}
